/**
 * Nearest point calculation for arc entities.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DrawingModuleType } from "../../DrawingSingleton";
import { deleteObj } from "../../core/delete";

/**
 * Get the closest point on an arc to a given point.
 * Uses OdGeCircArc3d for accurate arc geometry handling.
 *
 * @param module The ODA drawing module
 * @param arc The arc entity
 * @param givenPoint The point to find the closest point to
 * @returns The closest point on the arc, or null if not found
 */
export function getClosestPointToArc(
  module: DrawingModuleType,
  arc: DrawingModule.OdDbArc,
  givenPoint: DrawingModule.OdGePoint3d,
): DrawingModule.OdGePoint3d | null {
  const center = arc.center();
  const radius = arc.radius();
  const normal = arc.normal();
  const startAngle = arc.startAngle();
  const endAngle = arc.endAngle();

  // Create OdGeCircArc3d with angles
  // Use constructor: (center, normal, refVec, radius, startAng, endAng)
  const refVec = new module.OdGeVector3d(1, 0, 0); // X-axis reference
  const geArc = new module.OdGeCircArc3d(
    center,
    normal,
    refVec,
    radius,
    startAngle,
    endAngle,
  );

  const tol = new module.OdGeTol(1e-10);
  const closestPt = geArc.closestPointTo(givenPoint, tol);

  deleteObj(geArc);
  deleteObj(refVec);
  deleteObj(tol);
  deleteObj(normal);
  deleteObj(center);

  return closestPt;
}
